/**
 * useScreenProtection
 * ───────────────────
 * Core security hook: Immediately detects and blocks account on:
 *  - PrintScreen (keydown & keyup)
 *  - Alt+PrintScreen, Win+Shift+S, Cmd+Shift+3/4/5/6, Ctrl+P
 *  - Window Blur (user triggers Snipping Tool / external screenshot overlay)
 *  - Tab switch / Page visibility change
 *  - Screen sharing via getDisplayMedia
 *  - beforeprint event
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export interface ScreenProtectionState {
  isPageHidden: boolean;
  isScreenSharing: boolean;
  isPrintScreenAttempted: boolean;
  lockRemainingSeconds: number;
  isWindowBlurred: boolean;
  unlockScreenshotBlock: () => void;
}

interface UseScreenProtectionOptions {
  blockCopy?: boolean;
  blockContextMenu?: boolean;
  blockDrag?: boolean;
  flashOnPrintScreen?: boolean;
  screenshotLockDurationSec?: number;
  /** Enable window blur / focus-loss detection (default: true) */
  enableBlurDetection?: boolean;
  /** Block MediaRecorder API (screen recording via browser APIs) */
  blockMediaRecorder?: boolean;
  onScreenShareStart?: () => void;
  onScreenShareStop?: () => void;
  onPageHide?: () => void;
  onPageShow?: () => void;
  onPrintScreenAttempt?: () => void;
}

/** Flash a solid black overlay for `ms` milliseconds to dirty the captured frame. */
function flashBlackOverlay(ms = 800): void {
  const existing = document.getElementById('shine-prtsc-flash');
  if (existing) return;

  const overlay = document.createElement('div');
  overlay.id = 'shine-prtsc-flash';
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'width:100vw',
    'height:100vh',
    'z-index:2147483647',
    'background:#000000',
    'pointer-events:none',
    'opacity:1',
    'transition:opacity 0.2s ease',
  ].join(';');
  document.body.appendChild(overlay);

  setTimeout(() => {
    overlay.style.opacity = '0';
    setTimeout(() => overlay.remove(), 250);
  }, ms);
}

/** Overwrite the OS clipboard after a PrintScreen keypress. */
async function tryOverwriteClipboard(): Promise<void> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(
        '[🔒 Screenshot Blocked by Shine Exam Security — Account has been permanently suspended]'
      );
    }
  } catch {
    // Best-effort
  }
}

export function useScreenProtection(
  options: UseScreenProtectionOptions = {}
): ScreenProtectionState {
  const {
    blockCopy = true,
    blockContextMenu = true,
    blockDrag = true,
    flashOnPrintScreen = true,
    screenshotLockDurationSec = 300,
    enableBlurDetection = true,   // ← Default ON for protected modules
    blockMediaRecorder = true,
    onScreenShareStart,
    onScreenShareStop,
    onPageHide,
    onPageShow,
    onPrintScreenAttempt,
  } = options;

  const [isPageHidden, setIsPageHidden] = useState<boolean>(
    typeof document !== 'undefined' ? document.hidden : false
  );
  const [isScreenSharing, setIsScreenSharing] = useState<boolean>(false);
  const [isPrintScreenAttempted, setIsPrintScreenAttempted] = useState<boolean>(false);
  const [lockRemainingSeconds, setLockRemainingSeconds] = useState<number>(0);
  const [isWindowBlurred, setIsWindowBlurred] = useState<boolean>(false);

  const streamTrackerRef = useRef<MediaStream | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Prevent double-firing violation callbacks from keydown+keyup of same key event
  const lastViolationCallRef = useRef<number>(0);

  const unlockScreenshotBlock = useCallback(() => {
    setIsPrintScreenAttempted(false);
    setIsWindowBlurred(false);
    setLockRemainingSeconds(0);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const isInputTarget = (target: EventTarget | null): boolean => {
    if (!target) return false;
    const el = target as HTMLElement;
    return (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el.isContentEditable
    );
  };

  const preventCopy = useCallback((e: Event) => {
    if (isInputTarget(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const preventDrag = useCallback((e: Event) => {
    if (isInputTarget(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const preventContextMenu = useCallback((e: MouseEvent) => {
    if (isInputTarget(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // ── PrintScreen / Screenshot Detection ──────────────────────────────────
  const handlePrintScreenDetected = useCallback(() => {
    if (flashOnPrintScreen) flashBlackOverlay(800);
    tryOverwriteClipboard();

    setIsPrintScreenAttempted(true);
    setLockRemainingSeconds(screenshotLockDurationSec);

    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    let remaining = screenshotLockDurationSec;
    countdownIntervalRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        setIsPrintScreenAttempted(false);
        setLockRemainingSeconds(0);
      } else {
        setLockRemainingSeconds(remaining);
      }
    }, 1000);

    // Only call onPrintScreenAttempt once per 2 seconds to prevent keydown+keyup double-trigger
    const now = Date.now();
    if (now - lastViolationCallRef.current > 2000) {
      lastViolationCallRef.current = now;
      onPrintScreenAttempt?.();
    }
  }, [flashOnPrintScreen, screenshotLockDurationSec, onPrintScreenAttempt]);

  // ── Page Visibility change ──────────────────────────────────────────────
  useEffect(() => {
    const onVisibilityChange = () => {
      const hidden = document.hidden;
      setIsPageHidden(hidden);
      if (hidden) {
        onPageHide?.();
      } else {
        setIsWindowBlurred(false);
        onPageShow?.();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [onPageHide, onPageShow]);

  const lastMetaShiftTimeRef = useRef<number>(0);

  // ── Window blur/focus ──────────────────────────────────────────────────
  useEffect(() => {
    const onBlur = () => {
      if (!enableBlurDetection) return;
      // Do not treat focus shift to embedded iframe or when document has focus
      setTimeout(() => {
        if (typeof document !== 'undefined' && (document.hasFocus?.() || (document.activeElement && document.activeElement.tagName === 'IFRAME'))) {
          return;
        }
        setIsWindowBlurred(true);

        // If user pressed Win / Shift / S within 1500ms before focus loss, trigger instant screenshot violation!
        const timeSinceMetaShift = Date.now() - lastMetaShiftTimeRef.current;
        if (timeSinceMetaShift < 1500) {
          handlePrintScreenDetected();
        }
      }, 100);
    };
    const onFocus = () => {
      setIsWindowBlurred(false);
    };
    window.addEventListener('blur', onBlur, { capture: true });
    window.addEventListener('focus', onFocus, { capture: true });
    return () => {
      window.removeEventListener('blur', onBlur, { capture: true });
      window.removeEventListener('focus', onFocus, { capture: true });
    };
  }, [enableBlurDetection, handlePrintScreenDetected]);

  // ── Print event protection ──────────────────────────────────────────────
  // NOTE: beforeprint is skipped intentionally — some browsers fire it during
  // form submission network transitions which creates false positives.
  // Ctrl+P is already caught in the keyboard handler above.

  // ── Copy / drag / context-menu listeners ─────────────────────────────────
  useEffect(() => {
    if (blockCopy) {
      document.addEventListener('copy', preventCopy, { capture: true });
      document.addEventListener('cut', preventCopy, { capture: true });
    }
    if (blockDrag) {
      document.addEventListener('dragstart', preventDrag, { capture: true });
    }
    if (blockContextMenu) {
      document.addEventListener('contextmenu', preventContextMenu, { capture: true });
    }
    return () => {
      document.removeEventListener('copy', preventCopy, { capture: true });
      document.removeEventListener('cut', preventCopy, { capture: true });
      document.removeEventListener('dragstart', preventDrag, { capture: true });
      document.removeEventListener('contextmenu', preventContextMenu, { capture: true });
    };
  }, [blockCopy, blockContextMenu, blockDrag, preventCopy, preventDrag, preventContextMenu]);

  // ── Comprehensive Keyboard Guards ───────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key || '';
      const code = e.code || '';
      const keyCode = e.keyCode || e.which || 0;

      // Track whenever Win (Meta), Shift or Alt are engaged
      if (e.metaKey || e.shiftKey || key === 'Meta' || key === 'OS' || code === 'OSLeft' || code === 'OSRight') {
        lastMetaShiftTimeRef.current = Date.now();
      }

      // 1. PrintScreen (all variants, Snapshot, Print)
      if (key === 'PrintScreen' || code === 'PrintScreen' || key === 'Snapshot' || key === 'Print' || keyCode === 44) {
        e.preventDefault();
        e.stopPropagation();
        handlePrintScreenDetected();
        return;
      }
      // 2. Alt + PrintScreen
      if (e.altKey && (key === 'PrintScreen' || code === 'PrintScreen' || key === 'Snapshot' || keyCode === 44)) {
        e.preventDefault();
        e.stopPropagation();
        handlePrintScreenDetected();
        return;
      }
      // 3. Win+Shift+S / Meta+Shift+S / Ctrl+Shift+S / Alt+Shift+S (Windows Snipping Tool & Screen Snip)
      const isSKey = key.toLowerCase() === 's' || code === 'KeyS' || keyCode === 83;
      if (e.shiftKey && (e.metaKey || e.ctrlKey || e.altKey || key === 'Meta' || key === 'OS') && isSKey) {
        e.preventDefault();
        e.stopPropagation();
        handlePrintScreenDetected();
        return;
      }
      if ((e.metaKey || code === 'OSLeft' || code === 'OSRight') && (e.shiftKey || isSKey)) {
        lastMetaShiftTimeRef.current = Date.now();
        if (isSKey) {
          e.preventDefault();
          e.stopPropagation();
          handlePrintScreenDetected();
          return;
        }
      }
      // 4. macOS Cmd+Shift+3 / Cmd+Shift+4 / Cmd+Shift+5 / Cmd+Shift+6
      if (e.metaKey && e.shiftKey && ['3', '4', '5', '6', '#', '$', '%', '^'].includes(key)) {
        e.preventDefault();
        e.stopPropagation();
        handlePrintScreenDetected();
        return;
      }
      // 5. Ctrl+P / Cmd+P (Print)
      if ((e.ctrlKey || e.metaKey) && (key.toLowerCase() === 'p' || code === 'KeyP' || keyCode === 80)) {
        e.preventDefault();
        e.stopPropagation();
        handlePrintScreenDetected();
        return;
      }
      // 6. Ctrl+S (Save Page) — intentionally NOT blocked; browser save won't capture exam content
      // NOTE: We only block screenshot-specific combos above
      // 7. Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && (key.toLowerCase() === 'u' || code === 'KeyU')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // 8. F12 & DevTools Shortcuts
      if (key === 'F12' || code === 'F12' || keyCode === 123) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (key.toUpperCase() === 'I' || key.toUpperCase() === 'C' || key.toUpperCase() === 'J')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key || '';
      const code = e.code || '';
      const keyCode = e.keyCode || e.which || 0;
      if (key === 'PrintScreen' || code === 'PrintScreen' || key === 'Snapshot' || key === 'Print' || keyCode === 44) {
        handlePrintScreenDetected();
      }
      if (e.shiftKey && (e.metaKey || key === 'Meta' || key === 'OS') && (key.toLowerCase() === 's' || code === 'KeyS' || keyCode === 83)) {
        handlePrintScreenDetected();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    window.addEventListener('keyup', handleKeyUp, { capture: true, passive: false });
    document.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    document.addEventListener('keyup', handleKeyUp, { capture: true, passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener('keyup', handleKeyUp, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      document.removeEventListener('keyup', handleKeyUp, { capture: true });
    };
  }, [handlePrintScreenDetected]);

  // ── Screen Sharing Detection via MediaDevices ────────────────────────────
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;

    const originalGetDisplayMedia =
      navigator.mediaDevices.getDisplayMedia?.bind(navigator.mediaDevices);

    if (!originalGetDisplayMedia) return;

    navigator.mediaDevices.getDisplayMedia = async (
      constraints?: DisplayMediaStreamOptions
    ): Promise<MediaStream> => {
      const stream = await originalGetDisplayMedia(constraints);
      streamTrackerRef.current = stream;
      setIsScreenSharing(true);
      onScreenShareStart?.();

      stream.getTracks().forEach((track) => {
        track.addEventListener('ended', () => {
          const allEnded = stream.getTracks().every((t) => t.readyState === 'ended');
          if (allEnded) {
            streamTrackerRef.current = null;
            setIsScreenSharing(false);
            onScreenShareStop?.();
          }
        });
      });

      return stream;
    };

    return () => {
      if (originalGetDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia = originalGetDisplayMedia;
      }
      streamTrackerRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [onScreenShareStart, onScreenShareStop]);

  // ── MediaRecorder API Interception (screen recording detection) ──────────
  useEffect(() => {
    if (!blockMediaRecorder) return;
    if (typeof window === 'undefined' || !(window as any).MediaRecorder) return;

    const OriginalMediaRecorder = (window as any).MediaRecorder as typeof MediaRecorder;

    class PatchedMediaRecorder extends OriginalMediaRecorder {
      constructor(stream: MediaStream, options?: MediaRecorderOptions) {
        // Check if any track comes from a screen capture (display surface)
        const hasScreenTrack = stream.getVideoTracks().some((t) => {
          const settings = t.getSettings();
          return (settings as any).displaySurface !== undefined;
        });
        super(stream, options);
        if (hasScreenTrack) {
          // Notify that screen recording started
          setIsScreenSharing(true);
          onScreenShareStart?.();
          // Stop recording immediately
          try { (this as any).stop(); } catch {}
        }
      }
    }

    try {
      (window as any).MediaRecorder = PatchedMediaRecorder;
    } catch {
      // Some browsers don't allow MediaRecorder override — silent fail
    }

    return () => {
      try {
        (window as any).MediaRecorder = OriginalMediaRecorder;
      } catch {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockMediaRecorder, onScreenShareStart]);

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      document.getElementById('shine-prtsc-flash')?.remove();
    };
  }, []);

  return {
    isPageHidden,
    isScreenSharing,
    isPrintScreenAttempted,
    lockRemainingSeconds,
    isWindowBlurred,
    unlockScreenshotBlock,
  };
}
